// ModalWindow.ts
import { haveScreenAccess, LegacyModalWindowOpen, ModalWindowOpener, modalWindowUrl, windowInfo } from '../ChoiceUserConfiguration';
import { useScreensStore } from '../Stores/ScreensStore';
import { initPinia } from '../PiniaMgr';
import { ModalParent } from '../../Enums/UiEnums';

// Open a modal with a Vue screen as content
//	If the screen has a 'fall back' legacy screen, check to see if we should use the legacy screen or not.
export async function openVueModal(newModalWindow : ModalWindowOpener, modalParent: ModalParent = ModalParent.launchWindow): Promise<any> { 

	const screenStore = useScreensStore(initPinia());

	// Check if we should fallback to the legacy page
	const windowConfigInfo = windowInfo(newModalWindow.screenID);
	if (windowConfigInfo?.legacyScreenName)
	{
		if (await screenStore.useScreen(windowConfigInfo.legacyScreenName))
		{
			return await LegacyModalWindowOpen(newModalWindow);
		}
	}

	newModalWindow.vueModalOnly = true;
	newModalWindow.title = newModalWindow.title ?? windowInfo(newModalWindow.screenID)?.title;

	// See if we have security setup (either in the window setup or in the database)
	if (!windowConfigInfo?.securityPrivilegeType)
	{
		if (!await haveScreenAccess(newModalWindow.screenID))
		{
			alert("You do not have security for this window.");
			return;
		}
	}
    const url = modalWindowUrl(newModalWindow);
    if (!url)
    {
        alert(`Unable to generate URL for ${newModalWindow.screenID}`);
		return;
    }
	return setVueModal(url, newModalWindow.title, modalParent);
}

// Open a legacy modal window using the specified URL
export async function setVueModal(src: string, title: string|undefined, modalParent: ModalParent): Promise<any> {
	if (title) {
		return DisplayVueModalDiv(src,title, modalParent);
	}

	// TODO: Decide if we want to continue supporting DisplayVueModalIFrame()
	//return DisplayVueModalIFrame<TReturn>(src);
	alert("TODO: Handle IFrame");
}

// Add a transparent background
function AddModalBackground(parentDoc: Document) {
	let modalBackground = parentDoc.getElementById("iVueModal");
	if (!modalBackground) {
		modalBackground = parentDoc.createElement("IMG") as HTMLImageElement;
		modalBackground.id = "iVueModal";
		modalBackground.style.visibility = "visible";
		modalBackground.style.zIndex = "999";
		modalBackground.style.position = "absolute";
		modalBackground.style.left = "0%";
		modalBackground.style.top = "0%";
		modalBackground.style.width = "100%";
		modalBackground.style.height = "100%";
		parentDoc.body.appendChild(modalBackground); 

        const iVueModal = parentDoc.getElementById("iVueModal");
        if (!iVueModal)
        {
            alert("Failed attempt to get iVueModal");
            return;
        }
        iVueModal.style.background = "#f1f1f1";
        iVueModal.style.opacity = ".6";
		return;
	}
	modalBackground.style.visibility = "visible";
}

async function DisplayVueModalDiv(src: string, title: string, modalParent: ModalParent): Promise<any> {
	const parentDoc = modalParentWindow(modalParent).document;
	if (!parentDoc)
	{
		alert("Unable to get a valid paerent window");
		return;
	}
	AddVueModalDiv(parentDoc, src, title);

	return new Promise((resolve) => {
		// Get the 'close button' element and attach a listener
		//	Future:  We could add a key listener for an ESC button
		const closeButton = parentDoc.querySelector<HTMLElement>("#hVueModal > img");
		if (!closeButton)
		{
			alert("Couldn't find the #hVueModal > img");
			return;
		}
		closeButton.onclick = () =>
		{	
			// Get the return value
			const retValue = (window as any).returnValue;

			// Clean up
			removeModal(parentDoc);

			// Exit the promise
			resolve(retValue);
		}
	});
}

function AddVueModalDiv(mainDoc: Document, src: string, title: string) {
	let vueFrame = mainDoc.getElementById("fVueModal") as HTMLEmbedElement|null;
	let vueDiv = mainDoc.getElementById("dVueModal");
	let modal_percent = 90;
	const modal_xy_pos = (100 - modal_percent) / 2;
	AddModalBackground(mainDoc);
	if (!vueDiv) {
		vueDiv = mainDoc.createElement("DIV");
		vueDiv.id = "dVueModal";
		vueDiv.style.visibility = "visible";
		vueDiv.style.zIndex = "999";
		vueDiv.style.position = "absolute";
		vueDiv.style.left = modal_xy_pos + "%";
		vueDiv.style.top = modal_xy_pos + "%";
		vueDiv.style.width = modal_percent + "%";
		vueDiv.style.height = modal_percent + "%";

		if (vueFrame) {
			vueFrame.remove();
		}
		
		vueDiv.innerHTML = 
            `<DIV ID="hVueModal" name="hVueModal" style="background: #DBEBFF; border-bottom: 1px solid #6A8CCB; z-index: 999; POSITION: absolute; LEFT: 0px; TOP: 0px; WIDTH: 100%; HEIGHT: 23px;">
                <IMG src="/officemd/resources/img/icon/general/exit.png" style="position: absolute; top: 4px; right: 4px;" title="Close window" id="dialog-close"/>
                <SPAN style="padding-left: 5px; position: absolute; font-size: .8rem; top: 2px; color: #204D89; font-weight: bold;">${title}</SPAN>
            </DIV>
            <IFRAME ID="fVueModal" name="fVueModal" frameborder="0" src="${src}" style="z-index: 999; POSITION: absolute; LEFT: 0px; TOP: 24px; WIDTH: 99%; HEIGHT: 95%"></IFRAME>`;
	
		mainDoc.body.appendChild(vueDiv); 
        const dVueModal = mainDoc.getElementById("dVueModal");
        if (!dVueModal)
        {
            throw new Error("Error trying to get dVueModal");
        }
        dVueModal.style.border = "1px solid #6A8CCB";
        dVueModal.style.background = "white";
	}
	vueDiv.style.left = modal_xy_pos + "%";
	vueDiv.style.top = modal_xy_pos + "%";
	vueDiv.style.width = modal_percent + "%";
	vueDiv.style.height = modal_percent + "%";
	vueDiv.style.visibility = "visible";

    if (vueFrame)
    {
        vueFrame.src = src;
        vueFrame.style.visibility = "visible";
    }	
}

function removeModal(mainDoc: Document)
{
	// Clean up
	const vueDiv = mainDoc.getElementById("dVueModal");
	if (vueDiv)
		vueDiv.remove();
	const vueImg = mainDoc.getElementById("iVueModal");
	if (vueImg) 
		vueImg.remove();
}

function modalParentWindow(modalParent: ModalParent): Window
{
	switch(modalParent)
	{
		case ModalParent.appMain:
			{
				if (window.top?.frames)
				{
					for(const frameName in window.top.frames)
					{
						const frame = window.top.frames[frameName];
						if ("visible" === frame.document.visibilityState)
						{
							const fMain = parent.document.getElementById("fMain") as any;
							return fMain as Window;
						}
					}
				}
				return window.top as Window;
			}

		case ModalParent.launchWindow:
			return window;

		case ModalParent.appFull:
		default:
			return window.top as Window;
	}
}

// TODO: Need someone to tell me what parent_flag and parent_level mean (PaulW: Apr 14, 2023)
export function closeModal(parent_flag: boolean, parent_level?: number|undefined, returnValue?: unknown)
{
	let returnValueWindow = parent.window;
	parent_level = (parent_level == undefined ? 1 : parent_level);
	if ((parent_level == 1) && (parent_flag)) {
		returnValueWindow = parent.window;
	}
	else if ((parent_level == 2) && (parent_flag)) {
		returnValueWindow = parent.parent.window;
	}

	const closeButton = returnValueWindow.document.getElementById("dialog-close");
	if (!closeButton)
	{
		alert("Close button not found");
	}

	// Set the return value
	(returnValueWindow as any).returnValue = returnValue;

	// Close the window
	closeButton?.click();

	legacy__i_browser_dialog_close(parent_flag, parent_level);
}

// Simulate the legacy i_browser_dialog_close method in window.js
function legacy__i_browser_dialog_close(parent_flag: boolean,parent_level: number) {
	try {
		parent_level = (parent_level == undefined ? 1 : parent_level);
		if ((parent_level == 1) && (parent_flag)) {
			parent.document.getElementById('dialog-close')?.click();
		}
		else if ((parent_level == 2) && (parent_flag)) {
			parent.parent.document.getElementById('dialog-close')?.click();
		}
		else {
			document.getElementById('dialog-close')?.click();
		}
	} catch(e) {}	
}